import {
  Rocket as RocketIcon,
  Image as ImageIcon,
  VideoCall as VideoIcon,
  MenuBook as StoryIcon,
  Movie as ScreenplayIcon,
  Code as CodeIcon,
  MusicNote as MusicIcon,
  PhotoCamera as PhotoCameraIcon,
  Campaign as CampaignIcon,
  Psychology as PsychologyIcon,
  Brush as ComicsIcon,
  SmartToy as BotIcon,
  Science as ScienceIcon,
  DesignServices as DesignIcon,
  LocationCity as CityIcon,
  Checkroom as CheckroomIcon,
  Article as ArticleIcon,
  AutoStories as InteractiveIcon,
  AutoStories,
  ImageSearch as ImageSearchIcon,
  Analytics as AnalyticsIcon,
  Inventory2 as InventoryIcon,
  Storage as StorageIcon,
  CloudSync as CloudSyncIcon,
  Person as PersonIcon,
  Landscape as LandscapeIcon,
  MovieCreation as StoryboardIcon,
  Biotech as BiotechIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  Restaurant as RestaurantIcon,
  Style as StyleIcon,
} from '@mui/icons-material';

export interface AgentConfig {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: React.ComponentType<any>;
  tags: string[];
  isImplemented: boolean;
  category: 'content' | 'media' | 'marketing' | 'development' | 'general' | 'niche' | 'design' | 'studio';
}

export const agentConfigs: AgentConfig[] = [
  // Generic Studios - Multi-Agent Workflows
  {
    id: 'creative-studio',
    title: 'Creative Project Studio',
    description: 'End-to-end creative project management orchestrating multiple agents from concept to completion',
    route: '/creative-studio',
    icon: WorkIcon,
    tags: ['Multi-Agent', 'Workflow', 'Project Management', 'Creative'],
    isImplemented: true,
    category: 'studio',
  },
  {
    id: 'prompt-evolution',
    title: 'Prompt Evolution Studio',
    description: 'Evolve and refine prompts using a library of niche agents with specialized expertise',
    route: '/prompt-evolution',
    icon: PsychologyIcon,
    tags: ['Prompt Engineering', 'Evolution', 'Multi-Agent', 'Iteration'],
    isImplemented: true,
    category: 'studio',
  },
  {
    id: 'research-studio',
    title: 'Research & Intelligence Studio',
    description: 'Comprehensive research platform combining data analysis, expertise, and synthesis for intelligence reports',
    route: '/research-studio',
    icon: SearchIcon,
    tags: ['Research', 'Analysis', 'Intelligence', 'Multi-Agent'],
    isImplemented: true,
    category: 'studio',
  },
  {
    id: 'cuisine-prompt-studio',
    title: 'Cuisine Prompt Studio',
    description: 'Generate state-of-the-art culinary prompts using specialized cuisine agents and flavor profiling',
    route: '/cuisine-prompt-studio',
    icon: RestaurantIcon,
    tags: ['Cuisine', 'Food', 'Recipe Generation', 'Multi-Agent'],
    isImplemented: true,
    category: 'studio',
  },
  {
    id: 'story-prompt-studio',
    title: 'Story Prompt Studio',
    description: 'Transform stories, poems, and songs into coherent visual prompts with intelligent text partitioning',
    route: '/story-prompt-studio',
    icon: AutoStories,
    tags: ['Story', 'Visual Prompts', 'Text Analysis', 'Multi-Agent'],
    isImplemented: false,
    category: 'studio',
  },
  // Content Creation & Optimization
  {
    id: 'incubation',
    title: 'Incubation Studio',
    description: 'Transform your ideas into fully-architected systems through intelligent design workflows',
    route: '/incubation-studio',
    icon: RocketIcon,
    tags: ['System Design', 'Architecture', 'Full Stack'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'blog-multi-agent',
    title: 'Blog Multi-Agent',
    description: 'Generate comprehensive blog posts using multiple AI agents for research, writing, and editing',
    route: '/blog-multi-agent',
    icon: StoryIcon,
    tags: ['Blog Writing', 'Content Creation', 'Multi-Agent'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'prompt-improvement',
    title: 'Prompt Improvement',
    description: 'Refine and optimize your prompts for better AI responses across various models',
    route: '/prompt-improvement',
    icon: PsychologyIcon,
    tags: ['Prompt Engineering', 'AI Optimization', 'LLM'],
    isImplemented: false,
    category: 'content',
  },
  // Media Generation
  {
    id: 'image-prompt',
    title: 'Image Prompt Iterator',
    description: 'Generate and iterate on image prompts with AI agents for perfect visual descriptions',
    route: '/image-prompt-iterator',
    icon: ImageIcon,
    tags: ['Image Generation', 'Prompt Engineering', 'Creative'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'image-generation',
    title: 'Image Generation Studio',
    description: 'Generate stunning images with multiple AI providers including Flux, Luma, and Gemini',
    route: '/image-generation',
    icon: PhotoCameraIcon,
    tags: ['AI Art', 'Multi-Provider', 'Generation'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'video-prompt',
    title: 'Video Prompt Iterator',
    description: 'Create compelling video narratives and storyboards with AI-assisted prompt iteration',
    route: '/video-prompt-iterator',
    icon: VideoIcon,
    tags: ['Video Production', 'Storyboarding', 'Narrative'],
    isImplemented: true,
    category: 'media',
  },
  // Content & Marketing
  {
    id: 'social-media',
    title: 'Social Media Campaign',
    description: 'Create comprehensive social media campaigns with platform-specific content and engagement optimization',
    route: '/social-media-studio',
    icon: CampaignIcon,
    tags: ['Social Media', 'Marketing', 'Multi-Platform'],
    isImplemented: true,
    category: 'marketing',
  },
  {
    id: 'screenplay',
    title: 'Screenplay Generator',
    description: 'Write and structure professional screenplays with AI assistance',
    route: '/screenplay-generator',
    icon: ScreenplayIcon,
    tags: ['Screenwriting', 'Film', 'Narrative'],
    isImplemented: true,
    category: 'marketing',
  },
  {
    id: 'story-generator',
    title: 'Story Generator',
    description: 'Create engaging stories and narratives with AI-powered storytelling. Supports both single and multi-agent modes',
    route: '/story-generator',
    icon: StoryIcon,
    tags: ['Storytelling', 'Creative Writing', 'Multi-Agent'],
    isImplemented: false,
    category: 'marketing',
  },
  {
    id: 'comic-book',
    title: 'Comics Generator',
    description: 'Design and create comic book panels with AI-generated art and narratives',
    route: '/comic-book-creator',
    icon: ComicsIcon,
    tags: ['Comics', 'Storytelling', 'Visual Art'],
    isImplemented: true,
    category: 'marketing',
  },
  // Development & Creative Tools
  {
    id: 'code-improvement',
    title: 'AI Coding Assistant',
    description: 'Generate, improve, and iterate on code with AI assistance. Supports both single and multi-agent workflows',
    route: '/code-improvement',
    icon: CodeIcon,
    tags: ['Development', 'Code Generation', 'Multi-Agent'],
    isImplemented: true,
    category: 'development',
  },
  {
    id: 'tech-doc-multi-agent',
    title: 'Tech Documentation',
    description: 'Create comprehensive technical documentation with multiple AI agents specializing in different aspects',
    route: '/tech-doc-multi-agent',
    icon: CodeIcon,
    tags: ['Documentation', 'Technical Writing', 'Multi-Agent'],
    isImplemented: true,
    category: 'development',
  },
  {
    id: 'music-generator',
    title: 'Music & Sound Effect Studio',
    description: 'Create and iterate on music prompts and sound effects with specialized AI agents',
    route: '/music-prompt-iterator',
    icon: MusicIcon,
    tags: ['Music', 'Sound Effects', 'Audio Production'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'chat',
    title: 'General Chat',
    description: 'Have a conversation with AI for general assistance and information',
    route: '/chat',
    icon: BotIcon,
    tags: ['Chat', 'General AI', 'Assistant'],
    isImplemented: true,
    category: 'general',
  },
  // Niche Agents
  {
    id: 'niche-agents',
    title: 'Niche Agent Gallery',
    description: 'Access 18 specialized AI agents with 180 domain experts for deep visualization and analysis',
    route: '/niche-agents',
    icon: ScienceIcon,
    tags: ['Specialized', 'Deep Domain', 'Visualization'],
    isImplemented: true,
    category: 'niche',
  },
  // Design Tools
  {
    id: 'ux-designer',
    title: 'UI/UX Designer',
    description: 'Create comprehensive UX design artifacts including personas, user journeys, wireframes, mockups, and prototypes',
    route: '/ux-designer',
    icon: DesignIcon,
    tags: ['UX Design', 'Prototyping', 'User Research'],
    isImplemented: true,
    category: 'design',
  },
  {
    id: 'urban-agents',
    title: 'Urban Culture AI Studio',
    description: 'Specialized AI agents for urban culture visualization including animation, documentary, music, and street art',
    route: '/urban-agents',
    icon: CityIcon,
    tags: ['Urban Culture', 'Animation', 'Street Art', 'Music'],
    isImplemented: false,
    category: 'studio',
  },
  {
    id: 'fashion-multi-agent',
    title: 'Fashion Design Agent',
    description: 'Create comprehensive fashion collections with AI agents for trend analysis, design concepts, and technical specifications',
    route: '/fashion-multi-agent',
    icon: CheckroomIcon,
    tags: ['Fashion', 'Design', 'Trends', 'Multi-Agent'],
    isImplemented: true,
    category: 'design',
  },
  {
    id: 'fashion-design-studio',
    title: 'Fashion Design Studio',
    description: 'Professional fashion design with FLUX Krea & Kontext. Sketch-to-image, clothing swaps, 360 views, animations, and virtual try-on',
    route: '/fashion-design-studio',
    icon: StyleIcon,
    tags: ['Fashion', 'FLUX Krea', 'Design', '360 Views', 'Animation', 'Virtual Try-On'],
    isImplemented: true,
    category: 'studio',
  },
  {
    id: 'media',
    title: 'Media',
    description: 'Browse and view all your generated images and videos across all agents',
    route: '/media',
    icon: ImageSearchIcon,
    tags: ['Media', 'Gallery', 'Images', 'Videos', 'Assets'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'journalism-multi-agent',
    title: 'Journalism Multi-Agent',
    description: 'Write professional journalism articles with AI agents for research, fact-checking, and editorial polish',
    route: '/journalism-multi-agent',
    icon: ArticleIcon,
    tags: ['Journalism', 'News', 'Articles', 'Multi-Agent'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'interactive-fiction',
    title: 'Interactive Fiction Creator',
    description: 'Create branching narratives and interactive stories with multiple paths, choices, and game mechanics',
    route: '/interactive-fiction',
    icon: InteractiveIcon,
    tags: ['Interactive Fiction', 'Choose Your Own Adventure', 'Narrative', 'Game Design'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'novel-writing',
    title: 'Novel Writing Assistant',
    description: 'Create full-length novels with AI-powered chapter generation, character development, and world building',
    route: '/novel-writing',
    icon: StoryIcon,
    tags: ['Novel Writing', 'Book Creation', 'Long-form Writing', 'Character Development'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'abstract-thinker',
    title: 'Abstract Thinker',
    description: 'Explore complex concepts through iterative AI reasoning, philosophical inquiry, and abstract problem solving',
    route: '/abstract-thinker',
    icon: PsychologyIcon,
    tags: ['Philosophy', 'Abstract Reasoning', 'Conceptual Thinking', 'Problem Solving'],
    isImplemented: true,
    category: 'general',
  },
  {
    id: 'image-analysis',
    title: 'Image Analysis AI',
    description: 'Analyze images with AI-powered vision, detect objects, describe scenes, and generate cinematic summaries',
    route: '/image-analysis',
    icon: ImageSearchIcon,
    tags: ['Computer Vision', 'Image Analysis', 'Object Detection', 'Scene Description'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'data-analysis',
    title: 'Data Analysis Studio',
    description: 'Analyze RSS feeds and delimited data (CSV) with AI-powered insights, summaries, and trend detection',
    route: '/data-analysis-studio',
    icon: AnalyticsIcon,
    tags: ['Data Analysis', 'RSS Feeds', 'CSV Analysis', 'Insights'],
    isImplemented: true,
    category: 'development',
  },
  {
    id: 'content-pack',
    title: 'Content Pack Studio',
    description: 'Create reusable content packs including image prompts, general prompts, and social media campaigns',
    route: '/content-pack-studio',
    icon: InventoryIcon,
    tags: ['Content Packs', 'Batch Generation', 'Templates', 'Reusable Content'],
    isImplemented: false,
    category: 'content',
  },
  {
    id: 'simple-data',
    title: 'Simple Data Agent',
    description: 'Query, summarize, and generate FAQs from your data using AI-powered analysis',
    route: '/simple-data-agent',
    icon: StorageIcon,
    tags: ['Data Query', 'Summarization', 'FAQ Generation', 'CSV Analysis'],
    isImplemented: true,
    category: 'development',
  },
  {
    id: 'ragtag',
    title: 'RAG Tag Knowledge Base',
    description: 'Upload documents and query them using AI-powered semantic search and retrieval',
    route: '/ragtag-system',
    icon: CloudSyncIcon,
    tags: ['Document Search', 'Knowledge Base', 'RAG', 'Semantic Search'],
    isImplemented: true,
    category: 'development',
  },
  {
    id: 'character',
    title: 'Character Agent',
    description: 'Create and develop detailed characters with personality, backstory, and dynamic traits',
    route: '/character-agent',
    icon: PersonIcon,
    tags: ['Character Development', 'Creative Writing', 'Personality Design', 'Backstory'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'scene',
    title: 'Scene Visionary Agent',
    description: 'Create immersive scenes with atmosphere, sensory details, and character interactions',
    route: '/scene-agent',
    icon: LandscapeIcon,
    tags: ['Scene Creation', 'Atmosphere', 'Sensory Details', 'Setting Design'],
    isImplemented: true,
    category: 'content',
  },
  {
    id: 'storyboard',
    title: 'Animation Storyboard Agent',
    description: 'Design professional storyboards with scenes, shots, camera directions, and timing',
    route: '/storyboard-agent',
    icon: StoryboardIcon,
    tags: ['Storyboarding', 'Animation', 'Film Production', 'Shot Planning'],
    isImplemented: true,
    category: 'media',
  },
  {
    id: 'scientific',
    title: 'Scientific Agent Explorer',
    description: 'Access 30+ specialized scientific agents across engineering, life sciences, earth sciences, and social sciences',
    route: '/scientific-agent',
    icon: BiotechIcon,
    tags: ['Science', 'Research', 'Analysis', 'Multiple Domains'],
    isImplemented: true,
    category: 'general',
  },
  {
    id: 'paleontology',
    title: 'Paleontology Time Machine',
    description: 'Travel through Earth\'s history exploring ancient life from Precambrian to Cenozoic eras',
    route: '/paleontology-agent',
    icon: BiotechIcon,
    tags: ['Paleontology', 'Fossils', 'Evolution', 'Prehistoric Life'],
    isImplemented: true,
    category: 'niche',
  },
  // Individual Cuisine Agents - Hidden, using Cuisine Prompt Studio instead
  // {
  //   id: 'italian-cuisine',
  //   title: 'Italian Culinary Maestro',
  //   description: 'Master Italian cuisine from traditional family recipes to modern interpretations',
  //   route: '/italian-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['Italian Food', 'Recipes', 'Wine Pairings', 'Regional Dishes'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
  // {
  //   id: 'mexican-cuisine',
  //   title: 'Mexican Culinary Heritage',
  //   description: 'Explore the rich tapestry of Mexican cuisine, from ancient traditions to modern innovations',
  //   route: '/mexican-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['Mexican Food', 'UNESCO Heritage', 'Spices', 'Traditional'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
  // {
  //   id: 'indian-cuisine',
  //   title: 'Indian Culinary Traditions',
  //   description: 'Discover diverse Indian cuisine with Ayurvedic principles, regional specialties, and spice mastery',
  //   route: '/indian-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['Indian Food', 'Ayurveda', 'Spices', 'Vegetarian Excellence'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
  // {
  //   id: 'thai-cuisine',
  //   title: 'Thai Culinary Arts',
  //   description: 'Master the balance of sweet, sour, salty, and spicy in authentic Thai cuisine',
  //   route: '/thai-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['Thai Food', 'Flavor Balance', 'Fresh Herbs', 'Street Food'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
  // {
  //   id: 'japanese-cuisine',
  //   title: 'Japanese Culinary Excellence',
  //   description: 'Experience Japanese cuisine with emphasis on seasonality, presentation, and umami',
  //   route: '/japanese-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['Japanese Food', 'Sushi', 'Kaiseki', 'Washoku'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
  // {
  //   id: 'french-cuisine',
  //   title: 'French Haute Cuisine',
  //   description: 'Master French techniques from rustic regional dishes to haute cuisine',
  //   route: '/french-cuisine',
  //   icon: RestaurantIcon,
  //   tags: ['French Food', 'Classical Techniques', 'Wine Pairings', 'Michelin'],
  //   isImplemented: true,
  //   category: 'niche',
  // },
];

export const getAgentByRoute = (route: string): AgentConfig | undefined => {
  return agentConfigs.find(agent => agent.route === route);
};

export const getAgentsByCategory = (category: string): AgentConfig[] => {
  return agentConfigs.filter(agent => agent.category === category);
};

export const getImplementedAgents = (): AgentConfig[] => {
  return agentConfigs.filter(agent => agent.isImplemented);
};